import React from 'react';
import { FormAsyncMultiSelect } from '@getro/rombo';
import { Box, Text } from 'rebass/styled-components';
import PropTypes from 'prop-types';

import {
  getSearchEntryError,
  MAX_KEYWORD_LENGTH,
  SEARCH_ENTRY_ERRORS,
} from '../../../../../../helpers/booleanValidator';
import { useFormikJobTitleMultiSelect } from './useFormikJobTitleMultiSelect';
import { EditableMultiValueContainer } from './EditableMultiValueContainer';

export const FormikJobTitleMultiSelect = ({ name, sharedList, orderBy }) => {
  const {
    field,
    selected,
    selectRef,
    error,
    inputValue,
    handleEditValue,
    handleChange,
    handleInputChange,
    isValidNewOption,
    loadOptions,
  } = useFormikJobTitleMultiSelect({ name, sharedList, orderBy });

  // Entries hydrated from a saved or shared filter never pass through the input
  // handlers, so flag them rather than letting the search fail server-side.
  const oversizedSelected = selected.filter(
    (item) => getSearchEntryError((item.value || '').trim()) === SEARCH_ENTRY_ERRORS.tooLong,
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <FormAsyncMultiSelect
        ref={selectRef}
        name={name}
        field={{ ...field, value: selected, onChange: handleChange }}
        form={{}}
        label="Job title"
        placeholder="Select or press enter"
        size="small"
        loadOptions={loadOptions}
        formatCreateLabel={(val) => `Press 'enter' to add ${val}`}
        isValidNewOption={isValidNewOption}
        isCreatable
        anchorToBody
        allowCreateWhileLoading
        inputValue={inputValue}
        onInputChange={handleInputChange}
        components={{
          MultiValueContainer: EditableMultiValueContainer,
        }}
        onEditValue={handleEditValue}
        noOptionsMessage={({ inputValue: searchInput }) => {
          if (error) return null;
          return searchInput ? 'No options found' : 'Type to search...';
        }}
      />
      {error === SEARCH_ENTRY_ERRORS.tooLong && (
        <Text fontSize="12px" color="red.500" sx={{ mt: '-4px' }}>
          Job title is too long (max {MAX_KEYWORD_LENGTH} characters).
        </Text>
      )}
      {error === SEARCH_ENTRY_ERRORS.invalidBoolean && (
        <Text fontSize="12px" color="red.500" sx={{ mt: '-4px' }}>
          Invalid boolean format.{' '}
          <Box
            as="a"
            fontSize="12px"
            color="red.500"
            href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: 'underline' }}
          >
            Learn more
          </Box>
          .
        </Text>
      )}
      {oversizedSelected.length > 0 && (
        <Text fontSize="12px" color="red.500" sx={{ mt: '-4px' }}>
          {oversizedSelected.length === 1
            ? '1 selected job title is over'
            : `${oversizedSelected.length} selected job titles are over`}{' '}
          {MAX_KEYWORD_LENGTH} characters and will be rejected. Remove or shorten it to run the search.
        </Text>
      )}
      <Text sx={{ fontSize: '12px', color: 'text.subtle' }}>
        Learn to use boolean searches{' '}
        <Box
          as="a"
          color="text.subtle"
          href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
          target="_blank"
          rel="noreferrer"
        >
          here
        </Box>
        .
      </Text>
    </Box>
  );
};

FormikJobTitleMultiSelect.propTypes = {
  name: PropTypes.string.isRequired,
  sharedList: PropTypes.object,
  orderBy: PropTypes.oneOf(['count', 'alpha']),
};

FormikJobTitleMultiSelect.defaultProps = {
  sharedList: null,
  orderBy: 'count',
};
